import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Spinner } from 'react-bootstrap'
import { setDraggedFile, showMessageModal } from 'Manage/actions'
import { getCookie, setCookie } from 'utils/general'
import { getType } from 'mime-lite'
import './DropBoxConnector.css'
import { DROPBOX_SCRIPT_SRC, DROPBOX_APP_KEY } from 'constants'

const DropboxConnector = ({ onFileReady }) => {
  const dispatch = useDispatch()
  const [isDropboxLoading, setIsDropboxLoading] = useState(false)
  const scriptLoadedRef = useRef(false)

  const loadDropboxScript = () => {
    if (scriptLoadedRef.current) return Promise.resolve()

    return new Promise((resolve, reject) => {
      if (typeof window !== 'undefined' && window.Dropbox) {
        scriptLoadedRef.current = true
        resolve()
        return
      }

      const existingScript = document.querySelector(`script[src="${DROPBOX_SCRIPT_SRC}"]`)
      if (existingScript) {
        existingScript.addEventListener(
          'load',
          () => {
            scriptLoadedRef.current = true
            resolve()
          },
          { once: true }
        )
        existingScript.addEventListener(
          'error',
          () => {
            reject(new Error('Failed to load Dropbox SDK'))
          },
          { once: true }
        )
        return
      }

      const script = document.createElement('script')
      script.src = DROPBOX_SCRIPT_SRC
      script.async = true
      script.onload = () => {
        scriptLoadedRef.current = true
        resolve()
      }
      script.onerror = () => {
        reject(new Error('Failed to load Dropbox SDK'))
      }
      document.body.appendChild(script)
    })
  }

  const startDropboxOAuth = async (fileId) => {
    try {
      if (!window.Dropbox) {
        throw new Error('Dropbox SDK not loaded')
      }
      const { DropboxAuth } = window.Dropbox

      const dbxAuth = new DropboxAuth({ clientId: DROPBOX_APP_KEY })
      const redirectUri = `${window.location.origin}/manage/dropbox`
      const state = encodeURIComponent(JSON.stringify({ file_id: fileId }))

      const authUrl = await dbxAuth.getAuthenticationUrl(
        redirectUri,
        state,
        'code',
        'offline',
        undefined,
        undefined,
        true
      )

      sessionStorage.setItem('dropbox_code_verifier', dbxAuth.codeVerifier)
      window.location.href = authUrl
    } catch (error) {
      console.error('Error starting Dropbox OAuth:', error)
      setIsDropboxLoading(false)
      dispatch(
        showMessageModal({
          title: 'Dropbox Error',
          message: 'Failed to initiate Dropbox authentication. Please try again.',
          error: true
        })
      )
    }
  }

  const fetchDropboxFile = async (accessToken, fileId) => {
    setIsDropboxLoading(true)

    try {
      if (!window.Dropbox) {
        throw new Error('Dropbox SDK not loaded')
      }
      const { Dropbox } = window.Dropbox
      const dbx = new Dropbox({ accessToken })

      const res = await dbx.filesDownload({ path: fileId })
      const blob = res.result.fileBlob
      const name = res.result.name

      const file = new File([blob], name, {
        type: getType(name)
      })
      file.path = name

      setIsDropboxLoading(false)
      return file
    } catch (err) {
      console.error('Access token invalid or file fetch failed:', err)
      setIsDropboxLoading(false)
      startDropboxOAuth(fileId)
    }
  }

  const finishDropboxOAuth = async (fileId) => {
    setIsDropboxLoading(true)

    try {
      if (!window.Dropbox) {
        throw new Error('Dropbox SDK not loaded')
      }
      const { DropboxAuth } = window.Dropbox

      const params = new URLSearchParams(window.location.search)
      const code = params.get('code')
      const codeVerifier = sessionStorage.getItem('dropbox_code_verifier')

      if (!code || !codeVerifier) {
        console.error('Missing code or codeVerifier during Dropbox auth finish')
        setIsDropboxLoading(false)
        dispatch(
          showMessageModal({
            title: 'Dropbox Error',
            message: 'Authentication failed due to missing code or verifier. Please try again.',
            error: true
          })
        )
        return
      }

      const dbxAuth = new DropboxAuth({ clientId: DROPBOX_APP_KEY })
      dbxAuth.setCodeVerifier(codeVerifier)

      const response = await dbxAuth.getAccessTokenFromCode(`${window.location.origin}/manage/dropbox`, code)
      const token = response.result.access_token

      dbxAuth.setAccessToken(token)
      setCookie('dropbox_access_token', token, 7)

      const file = await fetchDropboxFile(token, fileId)
      setIsDropboxLoading(false)
      return file
    } catch (error) {
      console.error('Error during Dropbox OAuth finish:', error)
      setIsDropboxLoading(false)
      dispatch(
        showMessageModal({
          title: 'Dropbox Error',
          message: 'Failed to complete Dropbox authentication. Please try again.',
          error: true
        })
      )
      throw error
    }
  }

  useEffect(() => {
    const handleDropbox = async () => {
      // Only proceed if pathname is /manage/dropbox
      if (window.location.pathname !== '/manage/dropbox') {
        return
      }

      try {
        // Load the Dropbox script and wait for it to complete
        await loadDropboxScript()

        let file
        const urlParams = new URLSearchParams(window.location.search)
        const code = urlParams.get('code')
        const storedToken = getCookie('dropbox_access_token')
        let fileId = urlParams.get('file_id')

        if (!fileId && urlParams.get('state')) {
          try {
            const stateObj = JSON.parse(decodeURIComponent(urlParams.get('state')))
            fileId = stateObj.file_id
          } catch (e) {
            console.error('Failed to parse Dropbox state:', e)
            dispatch(
              showMessageModal({
                title: 'Dropbox Error',
                message: 'Failed to parse Dropbox state. Please try again.',
                error: true
              })
            )
            return
          }
        }

        if (fileId) {
          try {
            if (storedToken && !code) {
              file = await fetchDropboxFile(storedToken, fileId)
            } else if (code) {
              file = await finishDropboxOAuth(fileId)
            } else {
              await startDropboxOAuth(fileId)
              return
            }

            if (file) {
              dispatch(setDraggedFile(file))
              onFileReady()
            }
          } catch (error) {
            console.error('Error handling Dropbox file:', error)
            dispatch(
              showMessageModal({
                title: 'Dropbox Error',
                message: 'Failed to fetch file from Dropbox. Please try again.',
                error: true
              })
            )
          }
        }
      } catch (error) {
        console.error('Error loading Dropbox SDK:', error)
        dispatch(
          showMessageModal({
            title: 'Dropbox Error',
            message: 'Failed to load Dropbox SDK. Please try again.',
            error: true
          })
        )
      }
    }

    handleDropbox()

    // Cleanup to remove any lingering event listeners
    return () => {
      const existingScript = document.querySelector(`script[src="${DROPBOX_SCRIPT_SRC}"]`)
      if (existingScript) {
        existingScript.removeEventListener('load', () => {})
        existingScript.removeEventListener('error', () => {})
      }
    }
  }, [])

  return (
    <>
      {isDropboxLoading && (
        <div className="dropbox-loading-overlay">
          <div className="dropbox-loading-content">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Fetching file from Dropbox...</p>
          </div>
        </div>
      )}
    </>
  )
}

export default DropboxConnector
